import React, { useState } from 'react';
import APIService from '../services/api';
import { avecCle, useCleLecture } from '../services/cleLecture';
import { useFeatureOptIn, useFeatureApp, moduleAllowed } from '../services/features';
import { isAoAdmin } from './AoSociete';
import ErpiaBrand from './ErpiaBrand';
import { ancre } from '../tutoriel/ancres';
import { useTutoriel } from '../tutoriel/useTutoriel';
import { VERSION } from '../version';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style, className = '' }) => <i className={`bi bi-${icon} ${className}`} style={style} />;

// Horodatage relatif court pour les notifications
const fmtNotifDate = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(s)) return '';
  if (s < 60) return 'à l\'instant';
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.round(s / 3600)} h`;
  return `il y a ${Math.round(s / 86400)} j`;
};

export default function AtsSidebar({
  currentView, setCurrentView, currentUser, organisation, onLogout,
  theme, onToggleTheme,
  inAppNotifs = [], unreadCount = 0, onMarkAllRead, onMarkRead, onOpenNotif, notifDest,
}) {
  const [notifOpen, setNotifOpen] = useState(false);
  // Le bouton d'aide : un tutoriel qu'on ne peut relancer qu'une fois n'est
  // pas une aide, c'est une formalité. Il se cache tout seul quand le serveur
  // ne sert pas encore les parcours.
  const tuto = useTutoriel();
  // Un point sur l'entrée quand un parcours est commencé et pas fini : c'est
  // la seule chose que l'entrée doit dire en plus de son nom.
  const tutoEnCours = (tuto?.parcoursDispo || []).some(
    p => p.accessible && p.statut === 'en_cours' && p.progression?.faites > 0);
  const [logoErr, setLogoErr] = useState(false); // repli wordmark si pas de logo configuré
  // Le logo s'ouvre avec la CLÉ DE LECTURE (jamais le jeton de session dans
  // une adresse, 28.09) ; tant qu'elle n'est pas en main, la marque neutre.
  const cleL = useCleLecture();
  const logoUrl = cleL ? avecCle(`${BASE}/config/logo`) : null;
  // Tiroir mobile (≤ 820px) : la sidebar est hors écran, la barre supérieure
  // l'ouvre ; choisir un écran ou toucher le fond la referme.
  const [mobileOpen, setMobileOpen] = useState(false);
  // Sourcing LinkedIn : opt-in — l'entrée n'apparaît que si le flag est activé.
  // Sourceur : réservé aux admins — masquage d'interface, le 403 serveur
  // reste la vraie protection.
  const sourcingOrg = useFeatureOptIn('sourcing_linkedin');
  const sourceurOn = sourcingOrg && isAoAdmin(APIService.getCurrentUser());
  // Brique Ressources/Timesheet : app MARKETPLACE (état org, pas de bypass
  // admin) — l'entrée n'apparaît que si l'app « timesheet » est activée.
  // Droits par utilisateur : app activée au Marketplace ET non retirée à CE
  // user (Config → Utilisateurs → Modules). Admin : toujours tout.
  const ressourcesOn = useFeatureApp('ressources') && moduleAllowed(currentUser, 'ressources');
  // CRM (clients & missions) : app Marketplace « crm », même mécanique.
  const crmOn = useFeatureApp('crm') && moduleAllowed(currentUser, 'crm');
  // Forms : drapeau « forms », activé par défaut côté serveur.
  const formsOn = useFeatureApp('forms') && moduleAllowed(currentUser, 'forms');
  // Gestion de flotte : app Marketplace « tatlease ».
  const tatleaseOn = useFeatureApp('tatlease') && moduleAllowed(currentUser, 'tatlease');
  // DOCUMENTS À SIGNER : l'entrée n'a de sens que si l'organisation signe
  // électroniquement — sans le module, aucun document ne part et l'écran
  // serait une page vide dans la barre. L'ADRESSE, elle, reste servie : un
  // lien en favori ne doit pas casser parce qu'un module a été éteint.
  const signatureOn = useFeatureApp('docsign');
  // Rôle « ressource » (consultant en mission) : sidebar réduite à Mon espace —
  // aucun accès Besoins / Marketplace / Gestion.
  const isResource = currentUser?.role === 'ressource' || currentUser?.ressource === true;
  // Éditeur du produit (super admin) : la Roadmap. RIEN à voir avec admin, qui
  // administre UNE organisation — ce rôle-ci travaille au-dessus des tenants.
  // Masquage d'interface seulement : le 403 serveur reste la vraie protection.
  const editeur = currentUser?.super_admin === true;

  // NAVIGATION (vues) — base + admin
  const NAV = [
    { key: 'list', label: 'Besoins', icon: 'grid-3x3-gap-fill', active: currentView === 'list' || currentView === 'detail' },
    { key: 'kanban', label: 'Pipeline', icon: 'kanban-fill', active: currentView === 'kanban' },
    { key: 'vivier', label: 'Vivier RH', icon: 'people-fill', active: currentView === 'vivier' },
    { key: 'dossiers', label: 'Dossiers AO', icon: 'folder-fill', active: currentView === 'dossiers' },
    // Le calendrier est personnel (ses entretiens, ses agendas connectés) :
    // il reste dans la navigation principale, pas dans « Gestion ».
    { key: 'calendrier', label: 'Calendrier', icon: 'calendar-week', active: currentView === 'calendrier' },
  ];
  if (sourceurOn) NAV.push({ key: 'sourceur', label: 'Sourceur', icon: 'linkedin', active: currentView === 'sourceur' });
  // Candidats : visible pour tous les recruteurs (pas seulement l'admin) — seul
  // le rôle sourcer en est exclu. Marketplace et Config restent admin.
  if (currentUser?.role !== 'sourcer') {
    NAV.push({ key: 'candidats', label: 'Candidats', icon: 'person-vcard-fill', active: currentView === 'candidats' });
  }
  // QUALIFICATIONS — l'écran de contrôle : « est-ce que ça avance ? ». Ouvert
  // aux administrateurs ET aux commerciaux (responsables de besoins) : le
  // serveur décide ce que chacun voit (portée), l'entrée est juste visible.
  // Les recruteuses n'en ont pas besoin — leur liste à elles arrive par la
  // notification, sur le profil.
  // `business_manager` est le nom du rôle dans le produit. On testait
  // `role === 'commercial'` et `commercial === true` : deux valeurs qui
  // n'existent NULLE PART — ni en base, ni dans la réponse de login. Résultat,
  // seuls les administrateurs voyaient cet écran, alors qu'il a été fait pour
  // les business managers. Le serveur sert enfin le champ (PR serveur du même
  // lot) ; on le lit.
  if (currentUser?.admin || currentUser?.business_manager === true) {
    NAV.push({ key: 'qualifications', label: 'Qualifications', icon: 'clipboard-check-fill', active: currentView === 'qualifications' });
  }
  // Le suivi des documents envoyés à la signature — en attente, signé, refusé,
  // expiré. Ouvert à tout le monde sauf le sourceur : la recruteuse suit ses
  // NDA, le commercial ses contrats, chacun SES envois ; l'administrateur voit
  // ceux de toute l'organisation (règle serveur).
  if (signatureOn && currentUser?.role !== 'sourcer') {
    NAV.push({ key: 'documents', label: 'Suivi des documents', icon: 'pen', active: currentView === 'documents' });
  }
  // La liste noire — qui on ne contacte plus, d'où que vienne la décision
  // (fiche, campagne de sourcing, réponse par message, copilote). Pour tous
  // ceux qui contactent des candidats : le rôle sourcer en est exclu comme
  // de la liste Candidats.
  if (currentUser?.role !== 'sourcer') {
    NAV.push({ key: 'liste-noire', label: 'Liste noire', icon: 'slash-circle', active: currentView === 'liste-noire' });
  }
  // Les profils qu'on vous propose depuis une campagne de sourcing, à trier.
  // Pour tous ceux qui recrutent, dès que l'organisation a le sourcing — pas
  // seulement l'administrateur, qui seul voit l'écran Sourceur.
  if (sourcingOrg && currentUser?.role !== 'sourcer') {
    NAV.push({ key: 'propositions', label: 'Profils proposés', icon: 'inboxes', active: currentView === 'propositions' });
  }
  if (currentUser?.admin) {
    // Qui a fait quoi, et quand — écran, liens de document et copilote.
    NAV.push({ key: 'journal', label: "Journal d'activité", icon: 'journal-text', active: currentView === 'journal' });
    NAV.push({ key: 'marketplace', label: 'Marketplace', icon: 'shop', active: currentView === 'marketplace' });
    NAV.push({ key: 'config', label: 'Config', icon: 'gear-fill', active: currentView === 'config' });
  }

  const userInitial = (currentUser?.username || '?').charAt(0).toUpperCase();

  const sectionLabel = { fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--erp-sidebar-section, var(--text-3))', padding: '0 12px', margin: '0 0 7px' };
  const utilBtn = { width: '38px', height: '38px', borderRadius: '8px', border: '1px solid var(--erp-sidebar-border)', background: 'var(--erp-sidebar-control)', color: 'var(--erp-sidebar-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative' };

  // État actif porté par data-active + CSS (.ats-nav-item) — jamais par style inline (v2 §3)
  const Link = ({ item }) => (
    <button
      className="ats-nav-item"
      data-active={item.active ? 'true' : 'false'}
      onClick={() => { setMobileOpen(false); if (item.onClick) item.onClick(); else setCurrentView(item.key); }}
    >
      <BI icon={item.icon} style={{ fontSize: '16px', width: '16px', flex: 'none' }} />
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
      {item.pastille && (
        <span aria-hidden="true" style={{ marginLeft: 'auto', width: '6px', height: '6px', borderRadius: '50%',
          background: 'var(--erp-accent)', flex: 'none' }} />
      )}
    </button>
  );

  return (<>
    {/* Barre supérieure mobile — n'existe qu'en dessous de 820px (CSS). */}
    <div className="erp-mobile-topbar">
      <button onClick={() => setMobileOpen(true)} aria-label="Ouvrir le menu" title="Menu">
        <BI icon="list" />
      </button>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center' }}>
        {!logoErr && logoUrl
          ? <img src={logoUrl} alt="Logo" onError={() => setLogoErr(true)} style={{ maxHeight: '30px', maxWidth: '140px', objectFit: 'contain', display: 'block' }} />
          : <ErpiaBrand variant="sidebar" />}
      </div>
      <button onClick={() => { setMobileOpen(true); setNotifOpen(true); }} aria-label="Notifications" title="Notifications">
        <BI icon="bell" style={{ fontSize: '17px' }} />
        {unreadCount > 0 && (
          <span style={{ position: 'absolute', top: '9px', right: '10px', width: '7px', height: '7px', borderRadius: '50%', background: 'var(--dot)' }} />
        )}
      </button>
    </div>
    {mobileOpen && <button className="erp-sidebar-backdrop" aria-label="Fermer le menu" onClick={() => { setMobileOpen(false); setNotifOpen(false); }} />}
    <aside className={`erp-sidebar${mobileOpen ? ' erp-sidebar--open' : ''}`} aria-label="Navigation principale" style={{
      width: '216px', flex: 'none', position: 'sticky', top: 0, alignSelf: 'flex-start',
      height: '100vh', overflowY: 'auto', background: 'var(--erp-sidebar-bg)', borderRight: '1px solid var(--erp-sidebar-border)',
      padding: '18px 14px', display: 'flex', flexDirection: 'column', fontFamily: 'var(--erp-font-sans)',
    }}>
      {/* Logo — image configurée (org) sinon wordmark erpia */}
      <div style={{ padding: '0 6px', marginBottom: '22px', minWidth: 0 }}>
        {!logoErr && logoUrl
          ? <img src={logoUrl} alt="Logo" onError={() => setLogoErr(true)} style={{ maxHeight: '38px', maxWidth: '160px', objectFit: 'contain', display: 'block' }} />
          : <ErpiaBrand variant="sidebar" />}
        <div style={{ fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.11em', color: 'var(--brand)', marginTop: '5px' }}>Du besoin à la facture</div>
        {organisation?.nom && <div style={{ fontSize: '10.5px', fontWeight: 500, color: 'var(--erp-sidebar-text, var(--text-2))', marginTop: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={organisation.nom}>{organisation.nom}</div>}
      </div>

      {/* ÉDITEUR — la roadmap produit, hors de la navigation du client */}
      {editeur && !isResource && (
        <div style={{ marginBottom: '18px' }}>
          <p style={sectionLabel}>Éditeur</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <Link item={{ key: 'clients', label: 'Clients', icon: 'buildings', active: currentView === 'clients' }} />
            <Link item={{ key: 'roadmap', label: 'Roadmap', icon: 'map-fill', active: currentView === 'roadmap' }} />
            <Link item={{ key: 'sonde', label: 'Sonde', icon: 'clipboard-check', active: currentView === 'sonde' }} />
          </div>
        </div>
      )}

      {isResource ? (
        /* ESPACE RESSOURCE — Mon espace uniquement */
        <div style={{ marginBottom: '18px' }}>
          <p style={sectionLabel}>Mon espace</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {[
              { key: 'me-cra', label: 'Timesheet', icon: 'calendar3' },
              { key: 'me-conges', label: 'Congés', icon: 'sun' },
              { key: 'me-frais', label: 'Notes de frais', icon: 'credit-card' },
              { key: 'me-docs', label: 'Documents', icon: 'file-earmark-text' },
            ].map(it => <Link key={it.key} item={{ ...it, active: currentView === it.key }} />)}
          </div>
          <div style={{ marginTop: '16px', padding: '11px 12px', borderRadius: '10px', background: 'var(--erp-sidebar-control)', fontSize: '11.5px', lineHeight: 1.55, color: 'var(--erp-sidebar-muted)' }}>
            Besoin d'aide ? Contactez votre référent RH via votre agence.
          </div>
        </div>
      ) : (<>
        {/* NAVIGATION */}
        <div style={{ marginBottom: '18px' }}>
          <p style={sectionLabel}>Navigation</p>
          <div {...ancre('nav')} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            {NAV.map(item => <Link key={item.key} item={item} />)}
          </div>
        </div>

        {/* GESTION (revoir 29) — CRM & Ressources, gatés par leurs apps Marketplace */}
        {(ressourcesOn || crmOn || tatleaseOn || formsOn) && (
          <div style={{ marginBottom: '18px' }}>
            <p style={sectionLabel}>Gestion</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {crmOn && <Link item={{ key: 'crm', label: 'CRM', icon: 'briefcase-fill', active: currentView === 'crm' }} />}
              {/* Forms alimente la fiche ressource : même public que le CRM,
                  donc même porte, plus son propre drapeau (activé par défaut). */}
              {formsOn && <Link item={{ key: 'forms', label: 'Forms', icon: 'ui-checks', active: currentView === 'forms' }} />}
              {ressourcesOn && <Link item={{ key: 'ressources', label: 'Ressources', icon: 'person-gear', active: currentView === 'ressources' }} />}
              {tatleaseOn && <Link item={{ key: 'tatlease', label: 'Gestion de flotte', icon: 'truck', active: currentView === 'tatlease' }} />}
            </div>
          </div>
        )}
      </>)}

      {/* AIDE — le tutoriel a besoin d'une PORTE, pas d'une icône. Il vivait
          dans le pied, entre le thème et les notifications : personne ne l'y
          cherchait, et un parcours qu'on ne retrouve pas n'existe qu'une fois.
          Même grammaire que les autres sections, avec le point d'avancement
          sur l'entrée elle-même. */}
      {tuto?.disponible && !isResource && (
        <div style={{ marginBottom: '18px' }}>
          <p style={sectionLabel}>Aide</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <Link item={{ key: 'tutoriels', label: 'Tutoriels', icon: 'question-circle',
              active: tuto.panneauOuvert, pastille: tutoEnCours,
              onClick: () => tuto.basculerPanneau() }} />
          </div>
        </div>
      )}

      {/* Spacer */}
      <div style={{ flex: 1, minHeight: '16px' }} />

      {/* Utilitaires : thème + notifications + (placeholder réglages) */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', position: 'relative' }}>
        <button className="erp-sidebar-util" style={utilBtn} onClick={onToggleTheme} title={theme === 'dark' ? 'Thème clair' : 'Thème sombre'}>
          <BI icon={theme === 'dark' ? 'sun' : 'moon-stars'} style={{ fontSize: '16px' }} />
        </button>
        <button {...ancre('notifs')} style={utilBtn} onClick={() => setNotifOpen(o => !o)} title="Notifications">
          <BI icon="bell" style={{ fontSize: '16px' }} />
          {unreadCount > 0 && (
            <span style={{ position: 'absolute', top: '7px', right: '8px', width: '7px', height: '7px', borderRadius: '50%', background: 'var(--dot)', border: '1.5px solid var(--surface)' }} />
          )}
        </button>
        {currentUser?.admin && (
          <button style={utilBtn} onClick={() => setCurrentView('config')} title="Paramètres">
            <BI icon="gear" style={{ fontSize: '16px' }} />
          </button>
        )}

        {/* Dropdown notifications */}
        {notifOpen && (
          <div style={{ position: 'absolute', bottom: '46px', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: '0 8px 28px var(--hover-shadow)', zIndex: 50, maxHeight: '320px', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--hair)' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>Notifications</span>
              {unreadCount > 0 && <button onClick={onMarkAllRead} style={{ background: 'none', border: 'none', color: 'var(--text-2)', fontSize: '11.5px', cursor: 'pointer', fontFamily: 'inherit' }}>Tout marquer lu</button>}
            </div>
            {(!inAppNotifs || inAppNotifs.length === 0) ? (
              <p style={{ fontSize: '12.5px', color: 'var(--text-faint)', textAlign: 'center', padding: '20px 0' }}>Aucune notification</p>
            ) : inAppNotifs.map((n, i) => {
              const unread = !(n.lu || n.read);
              // Le lien annonce sa vraie destination (fiche, écran Sourceur…) —
              // et n'apparaît pas quand le clic n'ouvrirait rien : promettre
              // « Ouvrir la fiche » sur une fiche qui n'existe pas encore,
              // c'était le bug. notifDest (App) fait foi ; repli historique sinon.
              const dest = notifDest ? notifDest(n) : ((n.ref_id ?? n.refId) != null ? { label: 'Ouvrir la fiche →' } : null);
              return (
              <div key={n.id || i} onClick={() => { (onOpenNotif || onMarkRead)?.(onOpenNotif ? n : n.id); setNotifOpen(false); }}
                style={{ padding: '10px 14px', borderBottom: '1px solid var(--hair)', cursor: 'pointer', background: unread ? 'var(--accent-soft-bg)' : 'transparent', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                {unread && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--brand)', flex: 'none', marginTop: '5px' }} />}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '12.5px', color: 'var(--text)' }}>{n.message || n.text || n.body}</div>
                  {(n.date || n.created_at) && <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>{n.date || fmtNotifDate(n.created_at)}</div>}
                  {dest && <div style={{ fontSize: '11px', color: 'var(--accent-soft-fg, var(--brand))', marginTop: '2px' }}>{dest.label}</div>}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Filet */}
      <div style={{ borderTop: '1px solid var(--border)', marginBottom: '14px' }} />

      {/* Bloc compte */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '33px', height: '33px', borderRadius: '8px', background: 'var(--erp-sidebar-control)', color: 'var(--erp-sidebar-text)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace", flex: 'none' }}>
          {userInitial}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--erp-sidebar-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentUser?.username}</div>
          <div style={{ fontSize: '11px', color: 'var(--erp-sidebar-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentUser?.email}</div>
          {/* LA VERSION, en bas, là où l'œil finit : « on est sur la v0.1 » —
              elle vit dans src/version.js, une seule source. */}
          <div style={{ marginTop: '3px', fontSize: '10.5px', fontFamily: "'IBM Plex Mono', monospace", letterSpacing: '0.06em', color: 'var(--erp-sidebar-section, var(--text-3))', whiteSpace: 'nowrap' }}>
            ERPIA v{VERSION}
          </div>
        </div>
        {/* DÉCONNEXION — le symbole d'alimentation, pas la flèche qui sort
            d'une boîte. `box-arrow-right` est le glyphe par défaut de tous les
            gabarits Bootstrap : il ne dit rien de plus que sa position, et
            l'œil l'a déjà vu partout. Le cercle ouvert dit « ça s'arrête »
            sans un mot, et sa géométrie tient dans le système : un trait, un
            cercle, un rouge. Même signe dans l'en-tête — une action garde le
            même nom ET le même signe sur tout le parcours. */}
        <button onClick={onLogout} title="Déconnexion"
          style={{ width: '34px', height: '34px', borderRadius: '8px', border: '1px solid var(--danger-bd)', background: 'var(--danger-bg)', color: 'var(--danger-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none' }}>
          <BI icon="power" style={{ fontSize: '15px' }} />
        </button>
      </div>
    </aside>
  </>);
}
